import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Modal } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import { Spinner } from '@/shared/components/Spinner';
import { useStudents } from '@/features/director-students/hooks/useStudents';
import { useGrantManyMaterials } from '../hooks/useMaterialsAdmin';
import { normalize } from '../utils/normalize';
import { shiftLevel } from '@/shared/utils/shift';
import { StudentShiftBadge } from './StudentShiftBadge';
export function BulkPermissionsModal({ open, materials, onClose, onDone }) {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [error, setError] = useState(null);
    // All bulk-selected materials belong to the opposition shown in the page
    // (selection resets on opposition change), so scoping to the first one is safe.
    const oppositionId = materials[0]?.opposition_id;
    const { data: students = [], isLoading } = useStudents({
        status: 'active',
        ...(oppositionId ? { opposition_id: oppositionId } : {}),
    });
    const grantMany = useGrantManyMaterials();
    const filtered = useMemo(() => {
        const q = normalize(query.trim());
        if (!q)
            return students;
        return students.filter((s) => normalize(`${s.first_name} ${s.last_name} ${s.email}`).includes(q));
    }, [students, query]);
    const allFilteredSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
    function toggleStudent(id) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }
    function toggleAllFiltered() {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                for (const s of filtered)
                    next.delete(s.id);
            }
            else {
                for (const s of filtered)
                    next.add(s.id);
            }
            return next;
        });
    }
    // Selección rápida por nivel de turno (sobre el roster completo, no el filtrado).
    const inicioIds = students.filter((s) => shiftLevel(s.shift_name) === 'inicio').map((s) => s.id);
    const avanzadoIds = students.filter((s) => shiftLevel(s.shift_name) === 'avanzado').map((s) => s.id);
    function selectIds(ids) {
        setSelected((prev) => {
            const next = new Set(prev);
            for (const id of ids)
                next.add(id);
            return next;
        });
    }
    async function handleSubmit() {
        if (selected.size === 0) {
            setError('Selecciona al menos un alumno.');
            return;
        }
        setError(null);
        try {
            await grantMany.mutateAsync({
                materialIds: materials.map((m) => m.id),
                studentIds: Array.from(selected),
            });
            onDone();
        }
        catch {
            setError('Error concediendo permisos. Inténtalo de nuevo.');
        }
    }
    const summaryText = materials.length === 1
        ? `1 archivo: ${materials[0]?.name ?? ''}`
        : `${materials.length} archivos seleccionados`;
    return (_jsx(Modal, { open: open, onClose: onClose, title: "Conceder permisos", size: "lg", children: _jsxs("div", { className: "flex flex-col gap-3", children: [_jsxs("p", { className: "text-sm", style: { color: 'var(--fg-2)' }, children: [summaryText, ". Conceder\u00E1s acceso a los alumnos que selecciones."] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Input, { placeholder: "Buscar alumno por nombre o email\u2026", value: query, onChange: (e) => setQuery(e.target.value), className: "min-w-48 flex-1" }), _jsx(Button, { variant: "secondary", size: "sm", onClick: toggleAllFiltered, disabled: filtered.length === 0, children: allFilteredSelected ? 'Quitar todos' : 'Marcar todos' }), inicioIds.length > 0 && (_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => selectIds(inicioIds), children: ["Marcar Inicio (", inicioIds.length, ")"] })), avanzadoIds.length > 0 && (_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => selectIds(avanzadoIds), children: ["Marcar Avanzado (", avanzadoIds.length, ")"] }))] }), isLoading ? (_jsx("div", { className: "flex h-32 items-center justify-center", children: _jsx(Spinner, { size: "md" }) })) : (_jsxs("ul", { className: "max-h-72 divide-y overflow-y-auto rounded-lg border", style: { borderColor: 'var(--border)' }, children: [filtered.length === 0 && (_jsx("li", { className: "px-4 py-3 text-sm", style: { color: 'var(--fg-2)' }, children: "No hay alumnos que coincidan con la b\u00FAsqueda." })), filtered.map((s) => {
                            const checked = selected.has(s.id);
                            return (_jsx("li", { children: _jsxs("label", { className: "flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors", style: { background: checked ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'transparent' }, onMouseEnter: (e) => {
                                        if (!checked)
                                            e.currentTarget.style.background = 'var(--hover)';
                                    }, onMouseLeave: (e) => {
                                        if (!checked)
                                            e.currentTarget.style.background = 'transparent';
                                    }, children: [_jsx("input", { type: "checkbox", checked: checked, onChange: () => toggleStudent(s.id), className: "h-4 w-4 rounded" }), _jsxs("div", { className: "flex-1", children: [_jsxs("p", { className: "flex items-center gap-2 text-sm font-medium", style: { color: 'var(--fg)' }, children: [s.first_name, " ", s.last_name, _jsx(StudentShiftBadge, { shiftName: s.shift_name })] }), _jsx("p", { className: "text-xs", style: { color: 'var(--fg-2)' }, children: s.email })] })] }) }, s.id));
                        })] })), _jsxs("p", { className: "text-xs", style: { color: 'var(--fg-2)' }, children: [selected.size, " alumno", selected.size === 1 ? '' : 's', " seleccionado", selected.size === 1 ? '' : 's', "."] }), error && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: error })), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { variant: "secondary", onClick: onClose, children: "Cancelar" }), _jsxs(Button, { onClick: handleSubmit, loading: grantMany.isPending, disabled: selected.size === 0, children: ["Conceder a ", selected.size, " alumno", selected.size === 1 ? '' : 's'] })] })] }) }));
}
//# sourceMappingURL=BulkPermissionsModal.js.map